import { Blueprint } from '../../blueprint.class';
import { ToolResult } from '../../types';
import { RearrangeRefusedError, swapApplianceGroupPositionsAcrossPanelsCfn } from '../rearrange';

export interface SwapAppliancePositionsArgs {
	applianceId: string;
	otherApplianceId: string;
	/** Let a receiving island grow when the columns' widths differ and it has no room (default true; size-locked islands never grow) */
	autoSize?: boolean;
	/** Soft cap for a grown island, inches */
	maxWidth?: number | null;
}

/**
 * POSITIONAL swap: the two appliances (their whole columns) trade slots, on one
 * island or across two. Both stay in the kitchen. Not to be confused with
 * replace_appliance, which puts a different SKU in the same spot.
 */
export const swapAppliancePositionsTool = (blueprint: Blueprint, { applianceId, otherApplianceId, autoSize = true, maxWidth = null }: SwapAppliancePositionsArgs): ToolResult => {
	if (!applianceId || !otherApplianceId) return { success: false, error: 'Both applianceId and otherApplianceId are required. Use list_appliances to get valid item IDs.' };
	if (applianceId === otherApplianceId) return { success: false, error: 'applianceId and otherApplianceId must be different appliances.' };

	const kitchen = blueprint.kitchens.getActiveVersion();
	if (!kitchen) return { success: false, error: 'No active kitchen found.' };

	const refA = kitchen.getReferenceMap(applianceId);
	const refB = kitchen.getReferenceMap(otherApplianceId);
	if (!refA?.panelItemGroup || !refA.panel || !refA.island) return { success: false, error: `Appliance '${applianceId}' not found. Use list_appliances to get valid item IDs.` };
	if (!refB?.panelItemGroup || !refB.panel || !refB.island) return { success: false, error: `Appliance '${otherApplianceId}' not found. Use list_appliances to get valid item IDs.` };
	if (refA.panelItemGroup.id === refB.panelItemGroup.id) return { success: false, error: 'Both appliances are in the same column; there is nothing to swap.' };

	try {
		const growth = (islandLocked: boolean) => ({ growable: autoSize && !islandLocked, maxWidth });
		const result = swapApplianceGroupPositionsAcrossPanelsCfn(kitchen, refA.panelItemGroup, refB.panelItemGroup, {
			a: growth(refA.island.sizeLocked),
			b: growth(refB.island.sizeLocked)
		});
		const resizedNote = result.resized.length > 0
			? ` Resized: ${result.resized.map((r) => `${r.islandId} ${r.fromWidthIn}" -> ${r.toWidthIn}"`).join(', ')}.`
			: '';
		return { success: true, message: `Swapped the positions of '${applianceId}' and '${otherApplianceId}'.${resizedNote}`, data: result };
	} catch (error) {
		if (error instanceof RearrangeRefusedError) return { success: false, errorCode: error.reason, error: error.message };
		throw error;
	}
};
